import { useCallback, useEffect, useState } from 'react';
import { normalisePayToAccounts, type PayToAccount } from '@mypoz/shared';

import { fetchPayToRows } from '@/features/china-payment/api.js';

export interface PayToAccountsState {
  readonly status: 'loading' | 'ready' | 'error';
  /** Empty until ready. */
  readonly accounts: readonly PayToAccount[];
  readonly retry: () => void;
}

/** The accounts to pay into (MYP-61), loaded when a China Payment page opens. */
export function usePayToAccounts(): PayToAccountsState {
  const [status, setStatus] = useState<PayToAccountsState['status']>('loading');
  const [accounts, setAccounts] = useState<readonly PayToAccount[]>([]);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Only the newest attempt's answer lands (StrictMode's double mount, or a retry mid-flight).
    let current = true;
    setStatus('loading');
    fetchPayToRows().then(
      (rows) => {
        if (!current) return;
        setAccounts(normalisePayToAccounts(rows));
        setStatus('ready');
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[china-payment] could not load the accounts to pay into', cause);
        setStatus('error');
      },
    );
    return () => {
      current = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { status, accounts, retry };
}
